import React, { useState, useEffect, useRef } from 'react';

/**
 * The brand's wheel mascot — a purely geometric SVG spinner with personality
 * expressed through motion and proportions.
 *
 * Segments are alternating violet shades from the brand palette (#7c3aed base).
 * A triangular pointer at the top and a small hub "eye" in the center complete
 * the character. All animations are CSS-only and respect prefers-reduced-motion.
 */

const SEGMENT_COLORS = [
  '#7c3aed', // violet-600 (brand)
  '#a78bfa', // violet-400
  '#6d28d9', // violet-700
  '#c4b5fd', // violet-300
  '#5b21b6', // violet-800
  '#8b5cf6', // violet-500
  '#4c1d95', // violet-900
  '#ddd6fe', // violet-200
];

/** Builds a pie slice path for one segment of count total. */
function segmentPath(index: number, count: number, r: number, cx: number, cy: number): string {
  const angle = (2 * Math.PI) / count;
  const startAngle = index * angle - Math.PI / 2;
  const endAngle = startAngle + angle;
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle);
  const y2 = cy + r * Math.sin(endAngle);
  const largeArc = angle > Math.PI ? 1 : 0;
  return `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
}

interface WheelMascotProps {
  /** Diameter in CSS px. Default 48. */
  size?: number;
  /** When true the wheel rotates continuously. */
  spinning?: boolean;
  className?: string;
}

type Phase = 'idle' | 'spinning' | 'settling';

const WheelMascot: React.FC<WheelMascotProps> = ({
  size = 48,
  spinning = false,
  className = '',
}) => {
  const [phase, setPhase] = useState<Phase>(spinning ? 'spinning' : 'idle');
  const prevSpinning = useRef(spinning);

  useEffect(() => {
    // spin started
    if (spinning && !prevSpinning.current) {
      setPhase('spinning');
    }
    // spin stopped — play the settle
    if (!spinning && prevSpinning.current) {
      setPhase('settling');
      const timer = setTimeout(() => setPhase('idle'), 800); // 600ms settle + 200ms bounce
      return () => clearTimeout(timer);
    }
    prevSpinning.current = spinning;
  }, [spinning]);

  // On initial mount with spinning=true
  useEffect(() => {
    if (spinning) setPhase('spinning');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animationClass =
    phase === 'spinning'
      ? 'mascot-spin'
      : phase === 'settling'
        ? 'mascot-settle'
        : '';

  const count = SEGMENT_COLORS.length; // 8 segments
  const viewBox = 100;
  const cx = viewBox / 2;
  const cy = viewBox / 2;
  const r = 42; // wheel radius

  return (
    <svg
      viewBox={`0 0 ${viewBox} ${viewBox}`}
      width={size}
      height={size}
      className={`mascot-root ${className}`}
      aria-hidden="true"
      role="img"
    >
      {/* Wheel segments — inside a group so rotation applies to the wheel only */}
      <g className={`mascot-wheel ${animationClass}`} style={{ transformOrigin: `${cx}px ${cy}px` }}>
        {SEGMENT_COLORS.map((color, i) => (
          <path
            key={i}
            d={segmentPath(i, count, r, cx, cy)}
            fill={color}
            stroke="#1a1a2e"
            strokeWidth="0.5"
          />
        ))}

        {/* Outer rim */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#4c1d95" strokeWidth="1.5" opacity="0.4" />

        {/* Segment divider lines for visual clarity */}
        {SEGMENT_COLORS.map((_, i) => {
          const angle = (i * 2 * Math.PI) / count - Math.PI / 2;
          const x2 = cx + r * Math.cos(angle);
          const y2 = cy + r * Math.sin(angle);
          return (
            <line
              key={`div-${i}`}
              x1={cx}
              y1={cy}
              x2={x2}
              y2={y2}
              stroke="#1a1a2e"
              strokeWidth="0.6"
              opacity="0.3"
            />
          );
        })}
      </g>

      {/* Center hub "eye" */}
      <circle cx={cx} cy={cy} r="5" fill="#1a1a2e" />
      <circle cx={cx} cy={cy} r="3.5" fill="#e9d5ff" />
      {/* Motion blur ring — visible when spinning */}
      <circle
        cx={cx}
        cy={cy}
        r="4.5"
        fill="none"
        stroke="#c4b5fd"
        strokeWidth="1.5"
        className={phase === 'spinning' ? 'mascot-hub-blur' : ''}
        opacity={phase === 'spinning' ? 0.6 : 0}
      />
      {/* Inner dot */}
      <circle cx={cx} cy={cy} r="1.8" fill="#7c3aed" />

      {/* Pointer / pin at top — points DOWN into the wheel like a picker.
          Resting fill/stroke are segment 0's (what sits under it at angle 0);
          the phase classes animate them to track the spin and settle — see
          index.css "Pointer colour follows the segment under it". */}
      <polygon
        points={`${cx - 5},${cy - r - 8} ${cx + 5},${cy - r - 8} ${cx},${cy - r + 2}`}
        fill={SEGMENT_COLORS[0]}
        stroke="#ffffff"
        strokeWidth="0.8"
        strokeLinejoin="round"
        className={
          phase === 'spinning' ? 'mascot-pointer-spin'
          : phase === 'settling' ? 'mascot-pointer-settle'
          : ''
        }
      />
      {/* Pointer shadow */}
      <polygon
        points={`${cx - 5},${cy - r - 8} ${cx + 5},${cy - r - 8} ${cx},${cy - r + 2}`}
        fill="black"
        opacity="0.08"
        transform={`translate(0.5, 0.5)`}
      />
    </svg>
  );
};

export default WheelMascot;
